import { useCallback, useEffect, useRef, useState } from "react";

import type { MedplumClient } from "@medplum/core";
import type { Observation } from "@medplum/fhirtypes";

import { findRelatedPatientId } from "@/encounters/components/EncounterLabor/utils/related-patient";
import { fetchRelatedPatientPostpartumObservations } from "@/encounters/components/EncounterLabor/utils/related-patient-observations";

interface UseRelatedPatientPostpartumParams {
  medplum: MedplumClient;
  encounterId: string;
  clientId: string;
  isOptimistic: boolean | undefined;
  enabled?: boolean;
}

interface UseRelatedPatientPostpartumResult {
  relatedPatientId: string | null;
  fetchRelatedPatientObservations: (relatedPatientId: string) => Promise<Observation[]>;
}

export function useRelatedPatientPostpartum({
  medplum,
  encounterId,
  clientId,
  isOptimistic,
  enabled = true,
}: UseRelatedPatientPostpartumParams): UseRelatedPatientPostpartumResult {
  const [relatedPatientId, setRelatedPatientId] = useState<string | null>(null);
  const latestClientIdRef = useRef(clientId);

  useEffect(() => {
    latestClientIdRef.current = clientId;
  }, [clientId]);

  const resolveRelatedPatient = useCallback(
    async (patientId: string): Promise<string | null> =>
      findRelatedPatientId({ medplum, patientId, isOptimistic }),
    [isOptimistic, medplum],
  );

  useEffect(() => {
    if (!enabled) {
      setRelatedPatientId(null);
      return;
    }
    let isActive = true;
    const requestedClientId = clientId;

    if (requestedClientId) {
      void resolveRelatedPatient(requestedClientId).then((id) => {
        if (isActive && requestedClientId === latestClientIdRef.current) {
          setRelatedPatientId(id);
        }
      });
    } else {
      setRelatedPatientId(null);
    }

    return () => {
      isActive = false;
    };
  }, [clientId, enabled, resolveRelatedPatient]);

  const fetchRelatedPatientObservations = useCallback(
    async (relatedId: string): Promise<Observation[]> => {
      if (!enabled) {
        return [];
      }
      return fetchRelatedPatientPostpartumObservations({
        medplum,
        encounterId,
        relatedPatientId: relatedId,
        isOptimistic,
      });
    },
    [enabled, encounterId, isOptimistic, medplum],
  );

  return { relatedPatientId, fetchRelatedPatientObservations };
}
